/*
 * The single HTTP choke-point for the /x lane. Every /x call in webApp goes through xCall.
 * Owns: route (gate → /x vs legacy), the one isNotImplementedError, the 501→legacy fallback,
 * and a fallback-telemetry hook. This is the ONLY file that may import xEndpoints.
 */
import { X_ENDPOINTS } from './xEndpoints';

export function isNotImplementedError(err) {
  return Boolean(err && err.response && err.response.status === 501);
}

let fallbackReporter = null;
export function setFallbackReporter(fn) {
  fallbackReporter = typeof fn === 'function' ? fn : null;
}
function reportFallback(endpoint, stage) {
  try {
    if (fallbackReporter) fallbackReporter({ endpoint, stage });
  } catch (e) {
    /* telemetry must never break routing */
  }
}

/**
 * @param {object} client axios instance (state.client)
 * @param {string} key    manifest key (form|list|select|workspace)
 * @param {object} opts   { payload, gate, pathSuffix = '', config }
 *   - gate: required boolean for 'gated' endpoints; ignored for 'always-x-501only'
 */
export async function xCall(client, key, { payload, gate, pathSuffix = '', config } = {}) {
  const ep = X_ENDPOINTS[key];
  if (!ep) throw new Error(`xCall: unknown endpoint '${key}'`);
  if (ep.policy === 'gated' && typeof gate !== 'boolean') {
    throw new Error(`xCall('${key}'): gated endpoint requires an explicit gate decision (boolean)`);
  }
  const useX = ep.policy === 'always-x-501only' ? true : gate;

  // axios: get(url, config) takes no body; post(url, body, config) does.
  const send = (path) => ep.method === 'get'
    ? client.get(path + pathSuffix, config)
    : client.post(path + pathSuffix, payload, config);

  if (!useX) return send(ep.legacyPath);

  try {
    return await send(ep.xPath);
  } catch (err) {
    if (!isNotImplementedError(err)) throw err;           // 400/401/403/500/network -> surface
    console.warn(`[x-${key} fallback] 501 not_ported -> legacy`, err);
    reportFallback(key, 'fallback');
    try {
      return await send(ep.legacyPath);
    } catch (legacyErr) {
      reportFallback(key, 'stranded');                    // 501 but legacy also failed (§10)
      throw legacyErr;
    }
  }
}

/**
 * Workspace bootstrap helper (always-x-501only). `suffix` is the whole tail after the root and may
 * carry a query string ('0?vector=123'). Preserves the legacy getWorkspaceBootstrap behaviour.
 */
export async function getWorkspaceBootstrap(client, suffix, config) {
  return xCall(client, 'workspace', { pathSuffix: `/${suffix}`, config });
}
